/**
 * TableView test Adapters to abstract common unit test actions away from the implementation details.
 */
import { act, render, RenderResult, waitFor } from '@testing-library/preact';
import userEvent from '@testing-library/user-event';
import { expect } from 'chai';
import { VNode } from 'preact';
import { Cell } from '..';
import { getClientHints } from '../../utils/PRIVATE_clientHints';
import { matchTranslationBundle } from '../../utils/UNSAFE_matchTranslationBundle';
import { RootEnvironment, RootEnvironmentProvider } from '../../UNSAFE_Environment';

/**
 * Constant for timeout for longer running TableView tests
 */
const TABLEVIEW_LONG_TIMEOUT = 20000;
const TABLEVIEW_SHORT_TIMEOUT = 5000;

export const BORDER_WIDTH = 1;
export const BORDER_WIDTH_LAST = 2 * BORDER_WIDTH;

export const getShortTimeout = () => {
  return TABLEVIEW_SHORT_TIMEOUT;
};

/**
 * Helper method to render a TableView test component with the proper environment setup
 */
export const setupTableView = async (TestComponent: VNode) => {
  const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
  const { default: translations } = await import(`../../resources/nls/${locale}/bundle.ts`);
  const env: Partial<RootEnvironment> = {
    translations: { '@oracle/oraclejet-preact': translations }
  };
  return render(
    <RootEnvironmentProvider environment={env}>{TestComponent}</RootEnvironmentProvider>
  );
};

/**
 * Helper function to get all 'grid' role elements within the given rendered component.
 */
export const getInnerGrids = async (tableView: RenderResult) => {
  return tableView.findAllByRole('grid');
};

/**
 * Helper function to get all 'columnheader' role elements within the given rendered component.
 */
export const getColumnHeaders = async (tableView: RenderResult) => {
  return tableView.findAllByRole('columnheader');
};

/**
 * Helper function to get all 'row' role elements within the given rendered component.
 */
export const getRows = async (tableView: RenderResult) => {
  return tableView.findAllByRole('row');
};

/**
 * Helper function to get all data cell elements within the given data row.
 */
export const getDataCells = async (dataRow: Element) => {
  return dataRow.children;
};

/**
 * Helper function to get all footer elements within the given rendered component.
 */
export const getColumnFooters = async (tableView: RenderResult) => {
  const rows = await getRows(tableView);
  if (rows.length > 2) {
    return rows[rows.length - 1].children;
  }
  return [];
};

/**
 * Helper function to get the internal scroller element within the given rendered component.
 */
export const getInternalScroller = async (tableView: RenderResult) => {
  return tableView.findByRole('rowgroup');
};

/**
 * Helper method to find the containing cell element of a given element.
 */
export const findContainingCell = (
  rootElement: HTMLElement,
  contentElement: HTMLElement
): HTMLElement | null => {
  if (contentElement.dataset['ojCellType'] != null) {
    return contentElement;
  }
  const parentElement = contentElement.parentElement;
  if (parentElement === rootElement || parentElement == null) {
    return null;
  }
  return findContainingCell(rootElement, parentElement);
};

/**
 * Helper function to determine if the current environment is a Mac.
 */
export const isMac = () => {
  return getClientHints().platform === 'mac';
};

/**
 * Helper function to get the 'rowKey' test element.
 */
export const getRowKey = () => {
  return document.getElementById('rowKey');
};

/**
 * Helper function to get the 'columnKey' test element.
 */
export const getColumnKey = () => {
  return document.getElementById('columnKey');
};

/**
 * Helper function to get the 'cellType' test element.
 */
export const getCellType = () => {
  return document.getElementById('cellType');
};

/**
 * Helper function to verify the values of the Table's internal currentCell.
 */
export const verifyCurrentCellValue = async (value: Cell<string | number, string> | undefined) => {
  const expectedRowKey = value?.rowKey != null ? value.rowKey.toString() : '';
  const expectedColumnKey = value?.columnKey != null ? value.columnKey.toString() : '';
  const expectedCellType = value?.type != null ? value.type : '';
  await waitFor(
    () => {
      expect(getRowKey()?.textContent).to.equal(expectedRowKey);
    },
    { timeout: TABLEVIEW_SHORT_TIMEOUT }
  );
  await waitFor(
    () => {
      expect(getColumnKey()?.textContent).to.equal(expectedColumnKey);
    },
    { timeout: TABLEVIEW_SHORT_TIMEOUT }
  );
  await waitFor(
    () => {
      expect(getCellType()?.textContent).to.equal(expectedCellType);
    },
    { timeout: TABLEVIEW_SHORT_TIMEOUT }
  );
};

/**
 * Helper function to verify the values of the Table's internal currentCell have changed.
 */
export const verifyNotCurrentCellColumnValue = async (value: string | undefined) => {
  await waitFor(
    () => {
      expect(getColumnKey()?.textContent).to.not.equal(value);
    },
    { timeout: TABLEVIEW_SHORT_TIMEOUT }
  );
};

/**
 * Helper function to verify the values of the Table's internal currentCell have changed.
 */
export const verifyNotCurrentCellRowValue = async (value: string | number | undefined) => {
  await waitFor(
    () => {
      expect(getRowKey()?.textContent).to.not.equal(value);
    },
    { timeout: TABLEVIEW_SHORT_TIMEOUT }
  );
};

/**
 * Helper function to verify the values of aria-selected attributes on specific elements.
 */
export const verifyAriaSelected = async (
  selectedElements: Element[],
  unselectedElements: Element[]
) => {
  for (let i = 0; i < selectedElements.length; i++) {
    await waitFor(
      () => {
        expect(selectedElements[i].getAttribute('aria-selected')).to.equal('true');
      },
      { timeout: TABLEVIEW_SHORT_TIMEOUT }
    );
  }
  for (let i = 0; i < unselectedElements.length; i++) {
    await waitFor(
      () => {
        expect(unselectedElements[i].getAttribute('aria-selected')).to.not.equal('true');
      },
      { timeout: TABLEVIEW_SHORT_TIMEOUT }
    );
  }
};

/**
 * Helper function to verify the width of a Table element.
 */
export const verifyElementWidth = async (element: HTMLElement, width: number) => {
  await waitFor(
    () => {
      expect(element.offsetWidth).to.equal(width);
    },
    { timeout: TABLEVIEW_SHORT_TIMEOUT }
  );
};

/**
 * Helper function to verify the width of a Table element.
 */
export const determineBrowserScrollbarWidth = async (tableView: RenderResult) => {
  const grids = await getInnerGrids(tableView);

  const innerTable = grids[0].children[0].children[0].children[0] as HTMLElement;
  return grids[0].offsetWidth - innerTable.offsetWidth;
};

/**
 * Helper function to apply a longer timeout for tests
 */
export const enableLongTimeout = () => {
  if (typeof jest === 'undefined') {
    mocha.timeout(TABLEVIEW_LONG_TIMEOUT);
  } else {
    jest.setTimeout(TABLEVIEW_LONG_TIMEOUT);
  }
};

/**
 * Helper function to run a set of vertical scrolling tests.
 */
export const runVerticalScrollingTests = async (
  content: RenderResult,
  getViewportTop: () => number,
  getViewportBottom: () => number
) => {
  const user = userEvent.setup();

  console.log('viewport top');
  console.log(getViewportTop());
  console.log('viewport bottom');
  console.log(getViewportBottom());

  // expect 1 inner grid element
  const grids = await getInnerGrids(content);
  expect(grids.length).equals(1);

  const headers = await getColumnHeaders(content);
  const rows = await getRows(content);

  // focus first header cell
  await act(() => headers[0].focus());
  await verifyCurrentCellValue({ columnKey: 'id', type: 'header' });

  // determine first row that is not fully in view at bottom
  let rowBottom: number;
  let arrowCount = 0;
  for (let i = 1; i < rows.length; i++) {
    rowBottom = rows[i].getBoundingClientRect().bottom;
    if (rowBottom > getViewportBottom()) {
      arrowCount = i;
      break;
    }
  }

  // press arrow down repeatedly, and ensure focused cell is fully in view
  let rowKey = undefined;
  for (let i = 0; i < arrowCount; i++) {
    await user.keyboard('[ArrowDown]');
    await verifyNotCurrentCellRowValue(rowKey);
    rowKey = getRowKey()?.textContent;
    console.log(rowKey);
  }
  await waitFor(() => {
    rowBottom = rows[arrowCount].getBoundingClientRect().bottom;
    console.log('row bottom 1');
    console.log(rowBottom);
    expect(Math.abs(getViewportBottom() - rowBottom) <= 2).equals(true);
  });

  // ensure next 10 rows are scrolled into view
  for (let i = 1; i < 11; i++) {
    arrowCount += 1;
    await user.keyboard('[ArrowDown]');
    await verifyNotCurrentCellRowValue(rowKey);
    rowKey = getRowKey()?.textContent;
    console.log(rowKey);
    await waitFor(() => {
      rowBottom = rows[arrowCount].getBoundingClientRect().bottom;
      console.log('row bottom 2');
      console.log(rowBottom);
      expect(Math.abs(getViewportBottom() - rowBottom) <= 2).equals(true);
    });
  }

  // ensure next row is scrolled to top of viewport on page down
  let rowTop: number;
  await user.keyboard('[PageDown]');
  await verifyNotCurrentCellRowValue(rowKey);
  rowKey = getRowKey()?.textContent;
  console.log(rowKey);
  await waitFor(() => {
    rowTop = rows[arrowCount + 1].getBoundingClientRect().top;
    console.log('row top 3');
    console.log(rowTop);
    expect(Math.abs(getViewportTop() - rowTop) <= 2).equals(true);
  });

  // ensure previous row is scrolled to bottom of viewport on page up
  await user.keyboard('[PageUp]');
  await verifyNotCurrentCellRowValue(rowKey);
  rowKey = getRowKey()?.textContent;
  console.log(rowKey);
  await waitFor(() => {
    rowBottom = rows[arrowCount].getBoundingClientRect().bottom;
    console.log('row bottom 4');
    console.log(rowBottom);
    expect(Math.abs(getViewportBottom() - rowBottom) <= 2).equals(true);
  });

  // determine first row that is not fully in view at top
  let rowIndex: number;
  for (let i = arrowCount; i > 0; i--) {
    rowTop = rows[i].getBoundingClientRect().top;
    if (rowTop < getViewportTop()) {
      rowIndex = arrowCount - (arrowCount - i);
      arrowCount = arrowCount - i;
      break;
    }
  }

  // press arrow up repeatedly, and ensure focused cell is fully in view
  for (let i = 0; i < arrowCount; i++) {
    await user.keyboard('[ArrowUp]');
    await verifyNotCurrentCellRowValue(rowKey);
    rowKey = getRowKey()?.textContent;
    console.log(rowKey);
  }
  await waitFor(() => {
    rowTop = rows[rowIndex].getBoundingClientRect().top;
    console.log('row top 5');
    console.log(rowTop);
    expect(Math.abs(getViewportTop() - rowTop) <= 2).equals(true);
  });
};

/**
 * Helper function to run a set of horizontal scrolling tests.
 */
export const runHorizontalScrollingTests = async (
  content: RenderResult,
  getViewportStart: () => number,
  getViewportEnd: () => number,
  hasStickyColumns: boolean,
  isRtl: boolean,
  columnOrder: string[]
) => {
  const user = userEvent.setup();

  console.log('viewport start');
  console.log(getViewportStart());
  console.log('viewport end');
  console.log(getViewportEnd());

  // expect 1 inner grid element
  const grids = await getInnerGrids(content);
  expect(grids.length).equals(1);

  const headers = await getColumnHeaders(content);
  const columnsCount = headers.length;

  // focus first header cell
  await act(() => headers[0].focus());
  await verifyCurrentCellValue({ columnKey: 'id', type: 'header' });

  if (!isRtl) {
    // determine first column that is not fully in view at end
    let columnEnd: number;
    let arrowCount = 0;
    for (let i = 1; i < headers.length; i++) {
      columnEnd = headers[i].getBoundingClientRect().right;
      console.log('column end:');
      console.log(columnOrder[i]);
      console.log(columnEnd);
      console.log('is header cell same as initial:');
      const newHeaders = document.getElementsByTagName('th');
      console.log(headers[i] === newHeaders[i]);
      if (columnEnd > getViewportEnd()) {
        arrowCount = i;
        break;
      }
    }
    console.log('arrow count');
    console.log(arrowCount);

    // press arrow right repeatedly, and ensure focused cell is fully in view
    for (let i = 0; i < arrowCount; i++) {
      await user.keyboard('[ArrowRight]');
      await verifyCurrentCellValue({ columnKey: columnOrder[i + 1], type: 'header' });
      console.log(columnOrder[i + 1]);
    }
    await waitFor(() => {
      columnEnd = headers[arrowCount].getBoundingClientRect().right;
      console.log('column end 1');
      console.log(columnEnd);
      expect(Math.abs(getViewportEnd() - columnEnd) <= 2).equals(true);
    });

    // ensure next 2 columns are scrolled into view
    for (let i = 1; i < 3; i++) {
      arrowCount += 1;
      await user.keyboard('[ArrowRight]');
      await verifyCurrentCellValue({ columnKey: columnOrder[arrowCount], type: 'header' });
      console.log(columnOrder[arrowCount]);
      await waitFor(() => {
        columnEnd = headers[arrowCount].getBoundingClientRect().right;
        console.log('column end 2');
        console.log(columnEnd);
        expect(Math.abs(getViewportEnd() - columnEnd) <= 2).equals(true);
      });
    }

    let columnStart: number;
    let currentIndex: number;
    if (!hasStickyColumns) {
      // ensure first column is scrolled into view on home
      await user.keyboard('[Home]');
      await verifyNotCurrentCellColumnValue(columnOrder[arrowCount]);
      console.log(getColumnKey()?.textContent);
      await waitFor(() => {
        columnStart = headers[0].getBoundingClientRect().left;
        console.log('column start 3');
        console.log(columnStart);
        expect(Math.abs(getViewportStart() - columnStart) <= 2).equals(true);
      });

      // ensure last column is scrolled into view on end
      const columnKey = getColumnKey()?.textContent;
      await user.keyboard('[End]');
      await verifyNotCurrentCellColumnValue(columnKey);
      console.log(getColumnKey()?.textContent);

      await waitFor(() => {
        columnEnd = headers[columnsCount - 1].getBoundingClientRect().right;
        console.log('column end 4');
        console.log(columnEnd);
        expect(Math.abs(getViewportEnd() - columnEnd) <= 2).equals(true);
      });
      currentIndex = headers.length - 1;
    } else {
      currentIndex = arrowCount;
    }

    // determine first column that is not fully in view at start
    let columnIndex: number;
    for (let i = currentIndex; i >= 0; i--) {
      columnStart = headers[i].getBoundingClientRect().left;
      if (columnStart < getViewportStart()) {
        columnIndex = i;
        arrowCount = currentIndex - i;
        break;
      }
    }

    // press arrow left repeatedly, and ensure focused cell is fully in view
    for (let i = 0; i < arrowCount; i++) {
      await user.keyboard('[ArrowLeft]');
      await verifyCurrentCellValue({
        columnKey: columnOrder[currentIndex - i - 1],
        type: 'header'
      });
      console.log(columnOrder[currentIndex - i - 1]);
    }
    await waitFor(() => {
      columnStart = headers[columnIndex].getBoundingClientRect().left;
      console.log('column start 5');
      console.log(columnStart);
      expect(Math.abs(getViewportStart() - columnStart) <= 2).equals(true);
    });
  } else {
    // determine first column that is not fully in view at end
    let columnEnd: number;
    let arrowCount = 0;
    for (let i = 1; i < headers.length; i++) {
      columnEnd = headers[i].getBoundingClientRect().left;
      console.log('column end:');
      console.log(columnOrder[i]);
      console.log(columnEnd);
      console.log('is header cell same as initial:');
      const newHeaders = document.getElementsByTagName('th');
      console.log(headers[i] === newHeaders[i]);
      if (columnEnd < getViewportEnd()) {
        arrowCount = i;
        break;
      }
    }
    console.log('arrow count');
    console.log(arrowCount);

    // press arrow left repeatedly, and ensure focused cell is fully in view
    for (let i = 0; i < arrowCount; i++) {
      await user.keyboard('[ArrowLeft]');
      await verifyCurrentCellValue({ columnKey: columnOrder[i + 1], type: 'header' });
      console.log(columnOrder[i + 1]);
    }
    await waitFor(() => {
      columnEnd = headers[arrowCount].getBoundingClientRect().left;
      console.log('column end 6');
      console.log(columnEnd);
      expect(Math.abs(getViewportEnd() - columnEnd) <= 2).equals(true);
    });

    // ensure next 2 columns are scrolled into view
    for (let i = 1; i < 3; i++) {
      arrowCount += 1;
      await user.keyboard('[ArrowLeft]');
      await verifyCurrentCellValue({ columnKey: columnOrder[arrowCount], type: 'header' });
      console.log(columnOrder[arrowCount]);
      await waitFor(() => {
        columnEnd = headers[arrowCount].getBoundingClientRect().left;
        console.log('column end 7');
        console.log(columnEnd);
        expect(Math.abs(getViewportEnd() - columnEnd) <= 2).equals(true);
      });
    }

    let columnStart: number;
    let currentIndex: number;
    if (!hasStickyColumns) {
      // ensure first column is scrolled into view on home
      await user.keyboard('[Home]');
      await verifyNotCurrentCellColumnValue(columnOrder[arrowCount]);
      console.log(getColumnKey()?.textContent);
      await waitFor(() => {
        columnStart = headers[0].getBoundingClientRect().right;
        console.log('column start 8');
        console.log(columnStart);
        expect(Math.abs(getViewportStart() - columnStart) <= 2).equals(true);
      });

      // ensure last column is scrolled into view on end
      const columnKey = getColumnKey()?.textContent;
      await user.keyboard('[End]');
      await verifyNotCurrentCellColumnValue(columnKey);
      console.log(getColumnKey()?.textContent);
      await waitFor(() => {
        columnEnd = headers[columnsCount - 1].getBoundingClientRect().left;
        console.log('column end 9');
        console.log(columnEnd);
        expect(Math.abs(getViewportEnd() - columnEnd) <= 2).equals(true);
      });
      currentIndex = headers.length - 1;
    } else {
      currentIndex = arrowCount;
    }

    // determine first column that is not fully in view at start
    let columnIndex: number;
    for (let i = currentIndex; i >= 0; i--) {
      columnStart = headers[i].getBoundingClientRect().right;
      if (columnStart > getViewportStart()) {
        columnIndex = i;
        arrowCount = currentIndex - i;
        break;
      }
    }

    // press arrow right repeatedly, and ensure focused cell is fully in view
    for (let i = 0; i < arrowCount; i++) {
      await user.keyboard('[ArrowRight]');
      await verifyCurrentCellValue({
        columnKey: columnOrder[currentIndex - i - 1],
        type: 'header'
      });
      console.log(columnOrder[currentIndex - i - 1]);
    }
    await waitFor(() => {
      columnStart = headers[columnIndex].getBoundingClientRect().right;
      console.log('column start 10');
      console.log(columnStart);
      expect(Math.abs(getViewportStart() - columnStart) <= 2).equals(true);
    });
  }
};
